import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { 
  MapPin, 
  Clock, 
  Calendar, 
  Bus, 
  User, 
  CreditCard,
  CheckCircle,
  XCircle,
  Loader2,
  Search,
  ChevronDown,
  Circle,
  Navigation,
  X
} from 'lucide-react';
import Swal from 'sweetalert2';
import { routeApi, scheduleApi, cityApi, vehicleApi, bookingApi, customerApi, seatApi, routeCityApi } from '../services/api';
import { getSriLankaDateString, getMinSelectableDate, getMaxSelectableDate } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';
import OtpLoginModal from '../components/OtpLoginModal';
import MastercardPaymentModal from '../components/MastercardPaymentModal';
import paymentService from '../services/paymentService';

const BusBooking = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Initialize from location.state or URL params
  const getInitialRouteId = () => {
    const state = location.state;
    const urlRouteId = searchParams.get('routeId');
    return state?.routeId ? state.routeId.toString() : (urlRouteId || '');
  };
  
  // Get today's date in UTC format (YYYY-MM-DD)
  const getTodayUTCDate = () => {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const day = String(now.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get minimum selectable date in UTC (today)
  const getMinUTCDate = () => {
    return getTodayUTCDate();
  };

  const getInitialDate = () => {
    const state = location.state;
    const urlDate = searchParams.get('date');
    return state?.travelDate || urlDate || getTodayUTCDate();
  };

  const [selectedSeats, setSelectedSeats] = useState([]);
  const [passengerDetails, setPassengerDetails] = useState({
    name: '',
    email: '',
    phone: '',
    gender: '',
    nic: '',
    specialRequest: ''
  });
  const [pickupLocationId, setPickupLocationId] = useState('');
  const [dropoffLocationId, setDropoffLocationId] = useState('');
  const [routeCities, setRouteCities] = useState([]);
  const [routeCitiesLoading, setRouteCitiesLoading] = useState(false);
  // Removed bookingStep - passenger info is always visible now

  // Route and Date Selection States
  // Initialize from location.state or URL params if available, otherwise use defaults
  const [selectedRouteId, setSelectedRouteId] = useState(getInitialRouteId);
  const [selectedDate, setSelectedDate] = useState(getInitialDate);
  const [routes, setRoutes] = useState([]);
  const [routesLoading, setRoutesLoading] = useState(true);
  const [journeyDetails, setJourneyDetails] = useState(null);
  const [loadingJourney, setLoadingJourney] = useState(false);
  const [availableSchedules, setAvailableSchedules] = useState([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);

  // Searchable dropdown states for route
  const [routeSearchTerm, setRouteSearchTerm] = useState('');
  const [routeDropdownOpen, setRouteDropdownOpen] = useState(false);
  const routeDropdownRef = useRef(null);
  const bookButtonRef = useRef(null);

  const [seats, setSeats] = useState([]);
  // Use AuthContext for user state
  const { user } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentOrderId, setPaymentOrderId] = useState(null);
  const [pendingBookingData, setPendingBookingData] = useState(null);

  // Auto-fill passenger details from logged-in user
  useEffect(() => {
    if (user && user.fullName) {
      setPassengerDetails(prev => ({
        ...prev,
        name: prev.name || user.fullName || '',
        phone: prev.phone || user.phone || user.username || ''
      }));
    }
  }, [user]);

  // Generate seat layout based on vehicle seat layout (e.g., "4x12" = 4 columns × 12 rows)
  // Layout: columns x rows format from vehicle master
  // For 4x12: 4 columns total, all columns have seats (no skipping)
  // Seat numbers are sequential: 1, 2, 3, 4, 5, 6, etc.
  const generateSeats = (seatLayout, bookedSeats = [], notAvailableSeats = []) => {
    const seats = [];
    
    // Parse not available seats (comma-separated string like "1,5,11")
    const notAvailableSeatNumbers = notAvailableSeats
      .map(seat => seat.toString().trim())
      .filter(seat => seat !== '')
      .map(seat => parseInt(seat))
      .filter(seat => !isNaN(seat));
    
    // Check if this is the special 51 Layout
    if (seatLayout && seatLayout.toString().toLowerCase().includes('51 layout')) {
      // 51 Layout: Special arrangement - only actual seats, no empty cells
      // Seat 51 in the middle between left and right blocks
      // Layout: 4 columns (2 left, 1 middle for seat 51, 2 right)
      
      // Define seat positions - only actual seats, no empty cells
      const seatPositions = [
        // Row 1: Seat 51 in middle
        { row: 1, col: 1, seatNum: 47 }, { row: 1, col: 2, seatNum: 48 },
        { row: 1, col: 3, seatNum: 51 }, // Seat 51 in middle
        { row: 1, col: 4, seatNum: 50 }, { row: 1, col: 5, seatNum: 49 },
        // Row 2
        { row: 2, col: 1, seatNum: 41 }, { row: 2, col: 2, seatNum: 42 },
       // { row: 2, col: 3, seatNum: null }, // Aisle (skip this)
        { row: 2, col: 4, seatNum: 46 }, { row: 2, col: 5, seatNum: 45 },
        // Row 3
        { row: 3, col: 1, seatNum: 37 }, { row: 3, col: 2, seatNum: 38 },
       // { row: 3, col: 3, seatNum: null }, // Aisle (skip this)
        { row: 3, col: 4, seatNum: 44 }, { row: 3, col: 5, seatNum: 43 },
        // Row 4
        { row: 4, col: 1, seatNum: 33 }, { row: 4, col: 2, seatNum: 34 },
     //   { row: 4, col: 3, seatNum: null }, // Aisle (skip this)
        { row: 4, col: 4, seatNum: 40 }, { row: 4, col: 5, seatNum: 39 },
        // Row 5
        { row: 5, col: 1, seatNum: 29 }, { row: 5, col: 2, seatNum: 30 },
      // { row: 5, col: 3, seatNum: null }, // Aisle (skip this)
        { row: 5, col: 4, seatNum: 36 }, { row: 5, col: 5, seatNum: 35 },
        // Row 6
        { row: 6, col: 1, seatNum: 25 }, { row: 6, col: 2, seatNum: 26 },
      //  { row: 6, col: 3, seatNum: null }, // Aisle (skip this)
        { row: 6, col: 4, seatNum: 32 }, { row: 6, col: 5, seatNum: 31 },
        // Row 7
        { row: 7, col: 1, seatNum: 21 }, { row: 7, col: 2, seatNum: 22 },
      //  { row: 7, col: 3, seatNum: null }, // Aisle (skip this)
        { row: 7, col: 4, seatNum: 28 }, { row: 7, col: 5, seatNum: 27 },
        // Row 8
        { row: 8, col: 1, seatNum: 17 }, { row: 8, col: 2, seatNum: 18 },
      //  { row: 8, col: 3, seatNum: null }, // Aisle (skip this)
        { row: 8, col: 4, seatNum: 24 }, { row: 8, col: 5, seatNum: 23 },
        // Row 9
        { row: 9, col: 1, seatNum: 13 }, { row: 9, col: 2, seatNum: 14 },
//{ row: 9, col: 3, seatNum: null }, // Aisle (skip this)
        { row: 9, col: 4, seatNum: 20 }, { row: 9, col: 5, seatNum: 19 },
        // Row 10
        { row: 10, col: 1, seatNum: 9 }, { row: 10, col: 2, seatNum: 10 },
      //  { row: 10, col: 3, seatNum: null }, // Aisle (skip this)
        { row: 10, col: 4, seatNum: 16 }, { row: 10, col: 5, seatNum: 15 },
        // Row 11
        { row: 11, col: 1, seatNum: 5 }, { row: 11, col: 2, seatNum: 6 },
      //  { row: 11, col: 3, seatNum: null }, // Aisle (skip this)
        { row: 11, col: 4, seatNum: 12 }, { row: 11, col: 5, seatNum: 11 },
        // Row 12
        { row: 12, col: 1, seatNum: 1 }, { row: 12, col: 2, seatNum: 2 },
       // { row: 12, col: 3, seatNum: null }, // Aisle (skip this)
        { row: 12, col: 4, seatNum: 8 }, { row: 12, col: 5, seatNum: 7 },
        // Row 13
        { row: 13, col: 1, seatNum: null }, { row: 13, col: 2, seatNum: null },
       // { row: 13, col: 3, seatNum: null }, // Aisle (skip this)
        { row: 13, col: 4, seatNum: 4 }, { row: 13, col: 5, seatNum: 3 },
      ];
      
      // Process only actual seats (skip null/empty cells)
      seatPositions.forEach(({ row, col, seatNum }) => {
        if (seatNum !== null) {
          const seatNumberStr = seatNum.toString().padStart(2, '0');
          const isBooked = bookedSeats.some(bs => {
            if (!bs || !bs.seatNumber) return false;
            const bsSeatNumber = bs.seatNumber.toString().trim();
            return bsSeatNumber === seatNumberStr || 
                   bsSeatNumber === seatNum.toString() || 
                   parseInt(bsSeatNumber) === seatNum;
          });
          
          const bookedSeat = bookedSeats.find(bs => {
            if (!bs || !bs.seatNumber) return false;
            const bsSeatNumber = bs.seatNumber.toString().trim();
            return bsSeatNumber === seatNumberStr || 
                   bsSeatNumber === seatNum.toString() || 
                   parseInt(bsSeatNumber) === seatNum;
          });
          
          const isNotAvailable = notAvailableSeatNumbers.includes(seatNum);
          
          let isBookedByGents = false;
          let isBookedByLadies = false;
          let isAvailableSoon = false;
          
          if (isBooked && bookedSeat) {
            // Check for gender
            if (bookedSeat.gender) {
              const genderLower = bookedSeat.gender.toString().toLowerCase().trim();
              isBookedByGents = genderLower === 'male' || genderLower === 'm' || genderLower === 'gent' || genderLower === 'gents';
              isBookedByLadies = genderLower === 'female' || genderLower === 'f' || genderLower === 'lady' || genderLower === 'ladies';
            }
            
            // Check if PaymentStatus = 'Pending' AND Status = 'Pending'
            // Database columns: Bookings.PaymentStatus and Bookings.Status
            // Backend might return: paymentStatus (lowercase) and status (lowercase) or Status (capital)
            const paymentStatus = (bookedSeat.paymentStatus || bookedSeat.PaymentStatus || bookedSeat.Paymentstatus || bookedSeat.paymentstatus || '').toString().trim();
            const bookingStatus = (bookedSeat.status || bookedSeat.Status || bookedSeat.bookingStatus || bookedSeat.BookingStatus || bookedSeat.bookingstatus || '').toString().trim();
            
            // Debug log to check what fields are actually in the bookedSeat object
            console.log(`Seat ${seatNumberStr} bookedSeat object:`, bookedSeat);
            console.log(`Seat ${seatNumberStr}: paymentStatus="${paymentStatus}", status="${bookingStatus}"`);
            
            if (paymentStatus.toLowerCase() === 'pending' && bookingStatus.toLowerCase() === 'pending') {
              isAvailableSoon = true;
              console.log(`✅ Seat ${seatNumberStr} marked as Available Soon (Yellow) - paymentStatus: ${paymentStatus}, status: ${bookingStatus}`);
            }
          }
          
          seats.push({
            id: seatNumberStr,
            number: seatNumberStr,
            row: row,
            col: col,
            isAvailable: !isBooked && !isNotAvailable && !isAvailableSoon,
            isNotAvailable: isNotAvailable,
            isBookedByGents: isBookedByGents,
            isBookedByLadies: isBookedByLadies,
            isAvailableSoon: isAvailableSoon,
            isSelected: false
          });
        }
      });
      
      return seats;
    }
    
    // Standard layout parsing (e.g., "4x12" or "4×12")
    let columns = 4; // Default
    let rows = 12; // Default
    
    if (seatLayout) {
      const layoutMatch = seatLayout.toString().match(/(\d+)[x×](\d+)/i);
      if (layoutMatch) {
        columns = parseInt(layoutMatch[1]);
        rows = parseInt(layoutMatch[2]);
      }
    }
    
    // Generate seats with sequential numbering
    // For 4x12: Start from row 1, go left to right, top to bottom
    // Column order: 1, 2, 3, 4 (left to right)
    let seatNumber = 1;
    
    for (let row = 1; row <= rows; row++) {
      for (let col = 1; col <= columns; col++) {
        const seatNumberStr = seatNumber.toString();
        // More flexible matching - try both string and number comparison
        const isBooked = bookedSeats.some(bs => {
          if (!bs || !bs.seatNumber) return false;
          const bsSeatNumber = bs.seatNumber.toString().trim();
          return bsSeatNumber === seatNumberStr || 
                 bsSeatNumber === seatNumber.toString() || 
                 parseInt(bsSeatNumber) === seatNumber;
        });
        
        const bookedSeat = bookedSeats.find(bs => {
          if (!bs || !bs.seatNumber) return false;
          const bsSeatNumber = bs.seatNumber.toString().trim();
          return bsSeatNumber === seatNumberStr || 
                 bsSeatNumber === seatNumber.toString() || 
                 parseInt(bsSeatNumber) === seatNumber;
        });
        
        const isNotAvailable = notAvailableSeatNumbers.includes(seatNumber);
        
        // Determine gender-based booking status
        let isBookedByGents = false;
        let isBookedByLadies = false;
        let isAvailableSoon = false;
        
        if (isBooked && bookedSeat) {
          // Check for gender
          if (bookedSeat.gender) {
            const genderLower = bookedSeat.gender.toString().toLowerCase().trim();
            isBookedByGents = genderLower === 'male' || genderLower === 'm' || genderLower === 'gent' || genderLower === 'gents';
            isBookedByLadies = genderLower === 'female' || genderLower === 'f' || genderLower === 'lady' || genderLower === 'ladies';
          }
          
          // Check if Payment Status = 'Pending' AND Booking Status != 'Cancelled'
          const paymentStatus = (bookedSeat.paymentStatus || bookedSeat.PaymentStatus || '').toString().trim();
          const bookingStatus = (bookedSeat.bookingStatus || bookedSeat.BookingStatus || bookedSeat.status || bookedSeat.Status || '').toString().trim();
          
          if (paymentStatus.toLowerCase() === 'pending' && bookingStatus.toLowerCase() !== 'cancelled') {
            isAvailableSoon = true;
          }
        }
        
        seats.push({
          id: seatNumberStr,
          number: seatNumberStr,
          row: row,
          col: col,
          isAvailable: !isBooked && !isNotAvailable && !isAvailableSoon,
          isNotAvailable: isNotAvailable,
          isBookedByGents: isBookedByGents,
          isBookedByLadies: isBookedByLadies,
          isAvailableSoon: isAvailableSoon,
          isSelected: false
        });
        
        seatNumber++;
      }
    }
    return seats;
  };

  // Format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00');
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (routeDropdownRef.current && !routeDropdownRef.current.contains(event.target)) {
        setRouteDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Track if we've initialized from URL params (only once on mount)
  const [initialized, setInitialized] = useState(false);

  // Initialize from URL params or location.state if coming from Dashboard (only on mount)
  useEffect(() => {
    if (initialized) return; // Only initialize once
    
    const state = location.state;
    const urlRouteId = searchParams.get('routeId');
    const urlDate = searchParams.get('date');
    const urlScheduleId = searchParams.get('scheduleId');
    
    // Priority: URL params > location.state > current state
    // Use URL params first as they're more reliable
    if (urlRouteId) {
      setSelectedRouteId(urlRouteId);
    } else if (state?.routeId) {
      const newRouteId = state.routeId.toString();
      setSelectedRouteId(newRouteId);
    }
    
    if (urlDate) {
      setSelectedDate(urlDate);
    } else if (state?.travelDate) {
      setSelectedDate(state.travelDate);
    }
    
    setInitialized(true); // Mark as initialized
  }, []); // Only run once on mount

  // Load routes from API
  useEffect(() => {
    const loadRoutes = async () => {
      try {
        setRoutesLoading(true);
        const response = await routeApi.getRoutes(true); // Get only active routes
        
        let routesData = [];
        if (response?.value && Array.isArray(response.value)) {
          routesData = response.value;
        } else if (Array.isArray(response)) {
          routesData = response;
        } else if (response && typeof response === 'object') {
          routesData = [response];
        }
        
        setRoutes(routesData);
        
        // If we have a selectedRouteId from location.state but routes just loaded,
        // make sure it's still set (in case it was lost)
        const state = location.state;
        if (state?.routeId && !selectedRouteId) {
          console.log('Restoring routeId from location.state after routes loaded');
          setSelectedRouteId(state.routeId.toString());
        }
        if (state?.travelDate && selectedDate === getSriLankaDateString()) {
          console.log('Restoring travelDate from location.state after routes loaded');
          setSelectedDate(state.travelDate);
        }
      } catch (error) {
        console.error('Error loading routes:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load routes. Please refresh the page.',
          confirmButtonColor: '#dc2626'
        });
      } finally {
        setRoutesLoading(false);
      }
    };

    loadRoutes();
  }, []);

  // Fetch journey details when route or date changes
  useEffect(() => {
    if (selectedRouteId && selectedDate) {
      // Check for scheduleId from URL params or location.state
      const urlScheduleId = searchParams.get('scheduleId');
      const stateScheduleId = location.state?.scheduleId;
      const scheduleId = urlScheduleId || stateScheduleId;
      
      // If we have scheduleId and it matches the selected route, use it directly
      if (scheduleId && (location.state?.routeId?.toString() === selectedRouteId || urlScheduleId)) {
        fetchJourneyDetailsWithSchedule(scheduleId);
      } else {
        fetchJourneyDetails();
      }
    } else {
      setJourneyDetails(null);
      setSeats([]);
      setSelectedSeats([]);
      setAvailableSchedules([]);
      setSelectedScheduleId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRouteId, selectedDate]);

  // Load RouteCities when route is selected
  useEffect(() => {
    const loadRouteCities = async () => {
      if (!selectedRouteId) {
        setRouteCities([]);
        setPickupLocationId('');
        setDropoffLocationId('');
        return;
      }

      try {
        setRouteCitiesLoading(true);
        const response = await routeCityApi.getRouteCitiesByRoute(selectedRouteId);
        const cities = response || [];
        // Sort by order
        const sortedCities = cities.sort((a, b) => (a.order || a.Order || 0) - (b.order || b.Order || 0));
        setRouteCities(sortedCities);
      } catch (error) {
        console.error('Error loading route cities:', error);
        setRouteCities([]);
      } finally {
        setRouteCitiesLoading(false);
      }
    };

    loadRouteCities();
  }, [selectedRouteId]);

  // Helper function to process and set journey details
  const processJourneyDetails = async (selectedSchedule, scheduleDate, route) => {
    // Get vehicle details
    const vehicleId = selectedSchedule.busID || selectedSchedule.busId || selectedSchedule.vehicleID;
    let vehicle = null;
    let totalSeats = 48; // Default fallback
    let seatLayout = '4x12'; // Default layout
    let busNumber = ''; // Bus registration number
    let notAvailableSeats = []; // Not available seat numbers
    
    if (vehicleId) {
      try {
        const vehicleResponse = await vehicleApi.getVehicle(vehicleId);
        vehicle = vehicleResponse?.value || vehicleResponse;
        totalSeats = vehicle?.totalSeats || vehicle?.totalSeat || 48;
        seatLayout = vehicle?.seatLayout || '4x12';
        busNumber = vehicle?.registrationNumber || vehicle?.busNumber || '';
        // Parse not available seats (comma-separated string)
        // "0" means no unavailable seats
        const notAvailableSeatStr = vehicle?.notAvailableSeat || vehicle?.notAvailableSeats || '';
        if (notAvailableSeatStr && notAvailableSeatStr !== '0') {
          notAvailableSeats = notAvailableSeatStr.split(',').map(s => s.trim()).filter(s => s !== '' && s !== '0');
        }
      } catch (error) {
        console.warn('Could not fetch vehicle details:', error);
        // Use default or from schedule if available
        totalSeats = selectedSchedule.vehicle?.totalSeats || 48;
        seatLayout = selectedSchedule.vehicle?.seatLayout || '4x12';
        busNumber = selectedSchedule.vehicle?.registrationNumber || '';
        // "0" means no unavailable seats
        const notAvailableSeatStr = selectedSchedule.vehicle?.notAvailableSeat || '';
        if (notAvailableSeatStr && notAvailableSeatStr !== '0') {
          notAvailableSeats = notAvailableSeatStr.split(',').map(s => s.trim()).filter(s => s !== '' && s !== '0');
        }
      }
    }

    // Format time
    const formatTime = (timeString) => {
      if (!timeString) return 'N/A';
      try {
        const time = new Date(`2000-01-01T${timeString}`);
        return time.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        });
      } catch {
        return timeString;
      }
    };

    // Format duration
    const formatDuration = (minutes) => {
      if (!minutes) return 'N/A';
      if (minutes < 60) {
        return `${minutes} min`;
      }
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      if (remainingMinutes === 0) {
        return `${hours} hr`;
      }
      return `${hours} hr ${remainingMinutes} min`;
    };

      // Set journey details
      const journey = {
        route: `${route.fromLocation || route.fromCityName || 'Unknown'} → ${route.toLocation || route.toCityName || 'Unknown'}`,
        busType: selectedSchedule.scheduleName || 'Standard',
        busNumber: busNumber,
        date: formatDateForDisplay(selectedDate),
        departure: formatTime(scheduleDate.departureTime || selectedSchedule.departureTime),
        arrival: formatTime(scheduleDate.arrivalTime || selectedSchedule.arrivalTime),
        duration: formatDuration(route.estimatedDuration),
        price: `Rs ${(route.basePrice || 0).toFixed(2)}`,
        totalSeats: totalSeats,
        seatLayout: seatLayout,
        availableSeats: scheduleDate.availableSeats || 0,
        scheduleId: selectedSchedule.scheduleID || selectedSchedule.scheduleId,
        scheduleDateId: scheduleDate.scheduleDateID || scheduleDate.scheduleDateId,
        routeId: route.routeID || route.routeId,
        basePrice: route.basePrice || 0,
        serviceCharge: route.serviceCharge || 0
      };

      setJourneyDetails(journey);

      // Fetch booked seat numbers from schedule date API
      // The backend uses SQL: SELECT s.SeatNumber FROM [dbo].Bookings B 
      // LEFT JOIN [dbo].Seats s ON B.SeatID = s.SeatID WHERE B.ScheduleDateID = @scheduleDateId
      let bookedSeatNumbers = [];
      const scheduleDateId = scheduleDate.scheduleDateID || scheduleDate.scheduleDateId;
      
      if (scheduleDateId) {
        try {
          // Get schedule date details which includes booked seat numbers
          const scheduleDateResponse = await scheduleApi.getScheduleDate(scheduleDateId);
          const scheduleDateData = scheduleDateResponse?.value || scheduleDateResponse;
          
          // Extract booked seat numbers with gender from response
          // Check multiple possible response structures
          let bookedSeatsArray = null;
          
          if (scheduleDateData?.bookedSeatNumbers) {
            bookedSeatsArray = scheduleDateData.bookedSeatNumbers;
          } else if (scheduleDateData?.data?.bookedSeatNumbers) {
            bookedSeatsArray = scheduleDateData.data.bookedSeatNumbers;
          } else if (Array.isArray(scheduleDateData)) {
            // If response is directly an array
            bookedSeatsArray = scheduleDateData;
          }
          
          if (bookedSeatsArray && Array.isArray(bookedSeatsArray)) {
            // Check if bookedSeatNumbers is array of objects with gender or just strings
            if (bookedSeatsArray.length > 0 && typeof bookedSeatsArray[0] === 'object' && bookedSeatsArray[0] !== null) {
              // Array of objects with seatNumber and gender
              bookedSeatNumbers = bookedSeatsArray;
              // Log all booked seats received from backend
              console.log(`📋 Total booked seats received from backend: ${bookedSeatNumbers.length}`);
              bookedSeatNumbers.forEach((seat, index) => {
                console.log(`  [${index + 1}] Seat ${seat.seatNumber || seat.SeatNumber}: paymentStatus="${seat.paymentStatus || seat.PaymentStatus || 'N/A'}", status="${seat.status || seat.Status || 'N/A'}", gender="${seat.gender || seat.Gender || 'N/A'}"`);
              });
            } else {
              // Array of strings (backward compatibility)
              bookedSeatNumbers = bookedSeatsArray.map(s => ({
                seatNumber: s.toString().trim(),
                gender: null,
                paymentStatus: null,
                status: null
              }));
            }
          }
          
        } catch (error) {
          console.error('Could not fetch booked seat numbers:', error);
          console.error('Error details:', error.message, error.stack);
          // Continue with empty array if fetch fails
        }
      }

      // Format booked seats for generateSeats function
      // generateSeats expects array of objects with seatNumber, gender, paymentStatus, and bookingStatus properties
      const bookedSeats = bookedSeatNumbers.map(seat => {
        if (typeof seat === 'string') {
          return {
            seatNumber: seat.toString().trim(),
            gender: null,
            paymentStatus: null,
            bookingStatus: null
          };
        }
        return {
          seatNumber: (seat.seatNumber || seat.SeatNumber || seat).toString().trim(),
          gender: seat.gender || seat.Gender || null,
          paymentStatus: seat.paymentStatus || seat.PaymentStatus || null,
          bookingStatus: seat.bookingStatus || seat.BookingStatus || seat.status || seat.Status || null
        };
      });

      // Generate seats based on actual seat layout from vehicle master
      const generatedSeats = generateSeats(seatLayout, bookedSeats, notAvailableSeats);
      setSeats(generatedSeats);
      setSelectedSeats([]);
  };

  // Fetch journey details using specific scheduleId (when coming from Dashboard)
  const fetchJourneyDetailsWithSchedule = async (scheduleId) => {
    if (!selectedRouteId || !selectedDate) return;

    setLoadingJourney(true);
    setAvailableSchedules([]);
    setSelectedScheduleId(null);
    
    try {
      // First, fetch schedules filtered by date (performance optimization)
      // This only returns schedules that have ScheduleDates for the selected date
      const schedulesResponse = await scheduleApi.getSchedules(selectedDate);
      let schedules = [];
      
      if (schedulesResponse?.value && Array.isArray(schedulesResponse.value)) {
        schedules = schedulesResponse.value;
      } else if (Array.isArray(schedulesResponse)) {
        schedules = schedulesResponse;
      }

      // Filter schedules by route ID
      const routeSchedules = schedules.filter(s => 
        (s.routeID === parseInt(selectedRouteId) || s.routeId === parseInt(selectedRouteId)) && 
        (s.isActive !== false)
      );

      if (routeSchedules.length === 0) {
        // Don't show alert, just clear state - message will be shown in UI
        setJourneyDetails(null);
        setSeats([]);
        setAvailableSchedules([]);
        return;
      }

      // Get schedule dates for all schedules - now filtered by date at backend level
      // This dramatically reduces data transfer (1 record instead of 180+ records per schedule)
      const schedulesWithDates = [];
      
      for (const schedule of routeSchedules) {
        try {
          // Pass selectedDate to filter at backend - only returns ScheduleDate for that specific date
          const scheduleDatesResponse = await scheduleApi.getScheduleDatesBySchedule(
            schedule.scheduleID || schedule.scheduleId, 
            selectedDate
          );
          let scheduleDates = [];
          
          if (scheduleDatesResponse?.value && Array.isArray(scheduleDatesResponse.value)) {
            scheduleDates = scheduleDatesResponse.value;
          } else if (Array.isArray(scheduleDatesResponse)) {
            scheduleDates = scheduleDatesResponse;
          }

          // Since backend already filtered by date, we can directly use the first result
          // No need to filter again in frontend
          const matchingDate = scheduleDates.length > 0 ? scheduleDates[0] : null;

          if (matchingDate) {
            schedulesWithDates.push({
              schedule: schedule,
              scheduleDate: matchingDate,
              scheduleId: schedule.scheduleID || schedule.scheduleId,
              departure: formatTime(matchingDate.departureTime || schedule.departureTime),
              arrival: formatTime(matchingDate.arrivalTime || schedule.arrivalTime),
              price: matchingDate.price || schedule.price || 0,
              availableSeats: matchingDate.availableSeats || 0,
              scheduleName: schedule.scheduleName || 'Standard'
            });
          }
        } catch (error) {
          console.warn(`Error fetching schedule dates for schedule ${schedule.scheduleID}:`, error);
        }
      }

      if (schedulesWithDates.length === 0) {
        Swal.fire({
          icon: 'info',
          title: 'No Schedule for Date',
          text: 'No schedule available for the selected date.',
          confirmButtonColor: '#2563eb'
        });
        setJourneyDetails(null);
        setSeats([]);
        setAvailableSchedules([]);
        return;
      }

      // Store available schedules
      setAvailableSchedules(schedulesWithDates);

      // Find the selected schedule from the schedulesWithDates
      const selectedScheduleData = schedulesWithDates.find(sd => 
        (sd.scheduleId === parseInt(scheduleId)) || 
        (sd.schedule.scheduleID === parseInt(scheduleId)) ||
        (sd.schedule.scheduleId === parseInt(scheduleId))
      );

      if (selectedScheduleData) {
        // Set the selected schedule ID to highlight it
        setSelectedScheduleId(selectedScheduleData.scheduleId);
        
        // Get route details
        const routeResponse = await routeApi.getRoute(parseInt(selectedRouteId));
        const route = routeResponse?.value || routeResponse;

        // Load the selected schedule
        await processJourneyDetails(selectedScheduleData.schedule, selectedScheduleData.scheduleDate, route);
      } else {
        // If the scheduleId from URL doesn't match, use the first one
        const { schedule, scheduleDate } = schedulesWithDates[0];
        setSelectedScheduleId(schedule.scheduleID || schedule.scheduleId);
        
        // Get route details
        const routeResponse = await routeApi.getRoute(parseInt(selectedRouteId));
        const route = routeResponse?.value || routeResponse;

        await processJourneyDetails(schedule, scheduleDate, route);
      }

    } catch (error) {
      console.error('Error fetching journey details:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load journey details. Please try again.',
        confirmButtonColor: '#dc2626'
      });
      setJourneyDetails(null);
      setSeats([]);
      setAvailableSchedules([]);
    } finally {
      setLoadingJourney(false);
    }
  };

  // Format time helper
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    try {
      // If it's already in HH:MM format
      if (typeof timeString === 'string' && timeString.match(/^\d{1,2}:\d{2}$/)) {
        const [hours, minutes] = timeString.split(':');
        const hour12 = parseInt(hours) % 12 || 12;
        const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
        return `${hour12}:${minutes} ${ampm}`;
      }
      
      // If it's a datetime string
      if (timeString.includes('T') || timeString.includes(' ')) {
        const date = new Date(timeString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
          });
        }
      }
      
      // Try to parse as time string
      const date = new Date(`2000-01-01T${timeString}`);
      if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        });
      }
      
      return timeString;
    } catch (error) {
      return timeString;
    }
  };

  // Fetch journey details and seat information
  const fetchJourneyDetails = async () => {
    if (!selectedRouteId || !selectedDate) return;

    setLoadingJourney(true);
    setAvailableSchedules([]);
    setSelectedScheduleId(null);
    
    try {
      // Get schedules filtered by date (performance optimization)
      // This only returns schedules that have ScheduleDates for the selected date
      const schedulesResponse = await scheduleApi.getSchedules(selectedDate);
      let schedules = [];
      
      if (schedulesResponse?.value && Array.isArray(schedulesResponse.value)) {
        schedules = schedulesResponse.value;
      } else if (Array.isArray(schedulesResponse)) {
        schedules = schedulesResponse;
      }

      // Filter schedules by route ID
      const routeSchedules = schedules.filter(s => 
        (s.routeID === parseInt(selectedRouteId) || s.routeId === parseInt(selectedRouteId)) && 
        (s.isActive !== false)
      );

      if (routeSchedules.length === 0) {
        // Don't show alert, just clear state - message will be shown in UI
        setJourneyDetails(null);
        setSeats([]);
        setAvailableSchedules([]);
        return;
      }

      // Get schedule dates for all schedules - now filtered by date at backend level
      // This dramatically reduces data transfer (1 record instead of 180+ records per schedule)
      const schedulesWithDates = [];
      
      for (const schedule of routeSchedules) {
        try {
          // Pass selectedDate to filter at backend - only returns ScheduleDate for that specific date
          const scheduleDatesResponse = await scheduleApi.getScheduleDatesBySchedule(
            schedule.scheduleID || schedule.scheduleId, 
            selectedDate
          );
          let scheduleDates = [];
          
          if (scheduleDatesResponse?.value && Array.isArray(scheduleDatesResponse.value)) {
            scheduleDates = scheduleDatesResponse.value;
          } else if (Array.isArray(scheduleDatesResponse)) {
            scheduleDates = scheduleDatesResponse;
          }

          // Since backend already filtered by date, we can directly use the first result
          // No need to filter again in frontend
          const matchingDate = scheduleDates.length > 0 ? scheduleDates[0] : null;

          if (matchingDate) {
            schedulesWithDates.push({
              schedule: schedule,
              scheduleDate: matchingDate,
              scheduleId: schedule.scheduleID || schedule.scheduleId,
              departure: formatTime(matchingDate.departureTime || schedule.departureTime),
              arrival: formatTime(matchingDate.arrivalTime || schedule.arrivalTime),
              price: matchingDate.price || schedule.price || 0,
              availableSeats: matchingDate.availableSeats || 0,
              scheduleName: schedule.scheduleName || 'Standard'
            });
          }
        } catch (error) {
          console.warn(`Error fetching schedule dates for schedule ${schedule.scheduleID}:`, error);
        }
      }

      if (schedulesWithDates.length === 0) {
        Swal.fire({
          icon: 'info',
          title: 'No Schedule for Date',
          text: 'No schedule available for the selected date.',
          confirmButtonColor: '#2563eb'
        });
        setJourneyDetails(null);
        setSeats([]);
        setAvailableSchedules([]);
        return;
      }

      // Store available schedules
      setAvailableSchedules(schedulesWithDates);

      // If only one schedule, load it directly
      if (schedulesWithDates.length === 1) {
        const { schedule, scheduleDate } = schedulesWithDates[0];
        setSelectedScheduleId(schedule.scheduleID || schedule.scheduleId);
        
        // Get route details
        const routeResponse = await routeApi.getRoute(parseInt(selectedRouteId));
        const route = routeResponse?.value || routeResponse;

        await processJourneyDetails(schedule, scheduleDate, route);
      } else {
        // Multiple schedules - don't load yet, wait for user to select
        setJourneyDetails(null);
        setSeats([]);
      }

    } catch (error) {
      console.error('Error fetching journey details:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load journey details. Please try again.',
        confirmButtonColor: '#dc2626'
      });
      setJourneyDetails(null);
      setSeats([]);
      setAvailableSchedules([]);
    } finally {
      setLoadingJourney(false);
    }
  };

  // Handle schedule card click
  const handleScheduleSelect = async (scheduleData) => {
    setSelectedScheduleId(scheduleData.scheduleId);
    setLoadingJourney(true);
    
    try {
      const { schedule, scheduleDate } = scheduleData;
      
      // Get route details
      const routeResponse = await routeApi.getRoute(parseInt(selectedRouteId));
      const route = routeResponse?.value || routeResponse;

      await processJourneyDetails(schedule, scheduleDate, route);
    } catch (error) {
      console.error('Error loading schedule:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load schedule details. Please try again.',
        confirmButtonColor: '#dc2626'
      });
    } finally {
      setLoadingJourney(false);
    }
  };

  const handleSeatClick = (seat) => {
    // Don't allow clicking on booked seats, unavailable seats, or available soon seats
    if (!seat.isAvailable || seat.isNotAvailable || seat.isAvailableSoon) {
      return;
    }
    
    // Toggle seat selection - allow deselecting
    if (selectedSeats.includes(seat.id)) {
      // Deselect seat
      setSelectedSeats(prev => prev.filter(s => s !== seat.id));
      setSeats(prev => prev.map(s => 
        s.id === seat.id ? { ...s, isSelected: false } : s
      ));
    } else {
      // Select seat
      setSelectedSeats(prev => [...prev, seat.id]);
      setSeats(prev => prev.map(s => 
        s.id === seat.id ? { ...s, isSelected: true } : s
      ));
    }
  };

  const getSeatClass = (seat) => {
    // Selected seats - show in blue
    if (selectedSeats.includes(seat.id)) {
      return 'bg-blue-600 text-white cursor-pointer';
    }
    
    // Unavailable seats (not available for booking) - show in gray/ash color
    if (seat.isNotAvailable) {
      return 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-60';
    }
    
    // Seats with Payment Status = 'Pending' AND Booking Status != 'Cancelled' - show in Yellow
    if (seat.isAvailableSoon) {
      return 'bg-yellow-500 text-white cursor-not-allowed opacity-90';
    }
    
    // Booked seats - show in red/orange (not gray), cannot be selected
    if (!seat.isAvailable) {
      if (seat.isBookedByGents) {
        return 'bg-red-500 text-white cursor-not-allowed opacity-75';
      }
      if (seat.isBookedByLadies) {
        return 'bg-pink-500 text-white cursor-not-allowed opacity-75';
      }
      return 'bg-red-500 text-white cursor-not-allowed opacity-75';
    }
    
    // Available seats - show in green
    return 'bg-green-500 text-white cursor-pointer';
  };

  const handlePassengerDetailsChange = async (e) => {
    const { name, value } = e.target;
    setPassengerDetails(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-fill customer details when phone number is entered
    if (name === 'phone' && value && value.trim().length >= 10) {
      // Use a small delay to avoid too many API calls while typing
      const phoneValue = value.trim();
      setTimeout(async () => {
        // Only proceed if the phone value hasn't changed (user stopped typing)
        if (passengerDetails.phone === phoneValue) {
          try {
            // Fetch latest customer by phone number (backend returns the most recent one)
            const customerResponse = await customerApi.getCustomerByPhone(phoneValue);
            let customer = null;
            
            // Handle different response structures
            if (customerResponse?.value) {
              customer = customerResponse.value;
            } else if (customerResponse?.data) {
              customer = customerResponse.data;
            } else if (typeof customerResponse === 'object' && customerResponse.customerID) {
              customer = customerResponse;
            }

            // Auto-fill form fields with latest customer data
            if (customer) {
              setPassengerDetails(prev => ({
                ...prev,
                name: customer.customerName || customer.CustomerName || prev.name,
                email: customer.email || customer.Email || prev.email,
                phone: customer.phone || customer.Phone || phoneValue,
                gender: customer.gender || customer.Gender || prev.gender,
                nic: customer.nic || customer.NIC || prev.nic
              }));

            }
          } catch (error) {
            // Silently fail - don't show error if customer not found
            // This is expected if it's a new customer
          }
        }
      }, 500); // Wait 500ms after user stops typing
    }
  };

  // Removed handleContinue - no longer needed

  // User state is now managed by AuthContext, no need to load from localStorage here

  const handleBooking = async () => {
    // Check if user is logged in - directly show OTP modal if not logged in
    if (!user) {
        setShowLoginModal(true);
      return;
    }

    // Validate required passenger details (Name, Phone, Gender are mandatory)
    // Email and NIC are optional
    const missingFields = [];
      if (!passengerDetails.name || passengerDetails.name.trim() === '') {
        missingFields.push('Name');
      }
    if (!passengerDetails.phone || passengerDetails.phone.trim() === '') {
      missingFields.push('Phone Number');
    }
    if (!passengerDetails.gender || passengerDetails.gender.trim() === '') {
      missingFields.push('Gender');
    }

    // Validate Pickup Location is mandatory
    if (!pickupLocationId || pickupLocationId.trim() === '') {
      missingFields.push('Pickup Location');
    }

    // Validate Dropoff Location is mandatory
    if (!dropoffLocationId || dropoffLocationId.trim() === '') {
      missingFields.push('Dropoff Location');
    }

    if (missingFields.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Required Information',
        html: `<p style="margin-bottom: 10px;">Please fill in the following required fields:</p><ul style="text-align: left; margin: 10px 0; padding-left: 20px;"><li style="margin: 5px 0;"><strong>${missingFields.join('</strong></li><li style="margin: 5px 0;"><strong>')}</strong></li></ul><p style="margin-top: 10px; font-size: 0.9em; color: #666;">Note: Special Request is optional.</p>`,
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Validate Pickup Location OrderID < Dropoff Location OrderID
    if (pickupLocationId && dropoffLocationId) {
      const pickupLocation = routeCities.find(rc => (rc.cityID || rc.CityID)?.toString() === pickupLocationId);
      const dropoffLocation = routeCities.find(rc => (rc.cityID || rc.CityID)?.toString() === dropoffLocationId);
      
      if (pickupLocation && dropoffLocation) {
        const pickupOrderId = pickupLocation.orderID || pickupLocation.OrderID || 0;
        const dropoffOrderId = dropoffLocation.orderID || dropoffLocation.OrderID || 0;
        
        if (pickupOrderId >= dropoffOrderId) {
          Swal.fire({
            icon: 'warning',
            title: 'Invalid Location Selection',
            text: 'Pickup Location must come before Dropoff Location on the route. Please select locations in the correct order.',
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'OK'
          });
          return;
        }
      }
    }

    // Validate phone number format (should be at least 10 digits)
    const phoneRegex = /^[0-9]{10,}$/;
    const cleanPhone = passengerDetails.phone.replace(/[\s\-\(\)]/g, '');
    if (!phoneRegex.test(cleanPhone)) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Phone Number',
        text: 'Please enter a valid phone number (at least 10 digits).',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Validate journey and seats
    if (!journeyDetails || selectedSeats.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Selection',
        text: 'Please select seats to continue.',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
      });
      return;
    }

      // Calculate service charge with 15% discount applied
      const originalServiceCharge = journeyDetails.serviceCharge || 0; // Original service charge per seat
      const basePrice = parseFloat(journeyDetails.basePrice) || 0; // Single ticket price per seat
      const noOfTiket = selectedSeats.length; // Number of tickets/seats
      
      // Apply 15% discount to service charge
      const serviceCharge = originalServiceCharge * 0.85; // Discounted service charge per seat (15% off)
      const totalServiceCharge = serviceCharge * noOfTiket; // Total discounted service charge for all tickets
      
      // Calculate TotalAmount with discounted service charge
      const totalAmount = (basePrice + serviceCharge) * noOfTiket;

    try {
      // Show loading
      Swal.fire({
        title: 'Creating Booking...',
        text: 'Please wait while we prepare your booking.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Step 1: Find or create customer
      // Email and NIC are hidden from UI but sent with hardcoded null values
      const customerData = {
        CustomerName: passengerDetails.name,
        Phone: passengerDetails.phone,
        Email: null, // Hardcoded: Email field is hidden from UI
        Gender: passengerDetails.gender,
        NIC: null // Hardcoded: NIC field is hidden from UI
      };

      const customerResponse = await customerApi.findOrCreateCustomer(customerData);
      let customer = customerResponse?.value || customerResponse?.data || customerResponse;
      let customerId = customer?.customerID || customer?.CustomerID;

      if (!customerId) {
        throw new Error('Failed to create or find customer');
      }

      // Step 2: Get vehicle ID
      let vehicleId = null;
      if (journeyDetails.scheduleId) {
        const scheduleResponse = await scheduleApi.getSchedule(journeyDetails.scheduleId);
        const schedule = scheduleResponse?.value || scheduleResponse;
        vehicleId = schedule?.busID || schedule?.vehicleId || schedule?.vehicle?.id;
      }

      if (!vehicleId) {
        throw new Error('Could not determine vehicle ID for booking');
      }

      // Step 3: Create ONE booking record WITHOUT SeatID (seats will be in BookingSeats table)
      const travelDate = new Date(selectedDate + 'T00:00:00');
      
      // Validate required fields
      if (!journeyDetails.scheduleDateId) {
        throw new Error('Schedule Date ID is missing. Please select a journey date.');
      }
      
      // Ensure totalAmount is a valid number
      const bookingTotalAmount = typeof totalAmount === 'number' ? totalAmount : parseFloat(totalAmount) || 0;
      
      if (bookingTotalAmount <= 0) {
        throw new Error('Invalid total amount. Please check your seat selection.');
      }
      
      // Get UserID from user context or localStorage (fallback for cloud deployment)
      let userId = user?.id;
      if (!userId || userId <= 0) {
        // Fallback: Try to get from localStorage
        try {
          const savedUser = localStorage.getItem('user');
          if (savedUser) {
            const parsedUser = JSON.parse(savedUser);
            userId = parsedUser?.id || parsedUser?.userID || parsedUser?.UserID;
          }
        } catch (e) {
          console.warn('Could not parse user from localStorage:', e);
        }
      }
      
      // Create single booking record (NO SeatID - all seat info goes to BookingSeats table)
      // IMPORTANT: Explicitly include UserID to ensure it's set correctly (especially for cloud deployment)
      const bookingData = {
        CustomerID: parseInt(customerId),
        ScheduleDateID: parseInt(journeyDetails.scheduleDateId),
        SeatID: null, // No SeatID - use BookingSeats table instead
        TravelDate: travelDate.toISOString(),
        SpecialRequests: passengerDetails.specialRequest && passengerDetails.specialRequest.trim() !== '' 
          ? passengerDetails.specialRequest.trim() 
          : "",
        PaymentStatus: "Pending", // Set as Pending before payment
        TotalAmount: bookingTotalAmount, // Total for all seats - must be a number
        ServiceCharge: serviceCharge, // Single service charge per seat
        TicketPrice: basePrice, // Single ticket price per seat
        NoOfTiket: noOfTiket, // Number of tickets/seats
        TotalServiceCharge: totalServiceCharge, // Total service charge for all tickets (ServiceCharge * NoOfTiket)
        UserID: userId || null, // Explicitly set UserID from logged-in user (fallback for cloud if JWT claims fail)
        AdminBooked: 0, // 0 for passenger bookings (1 for admin bookings)
        PaymentMethod: "WEB", // WEB for passenger bookings made through the website
        PickupLocationID: pickupLocationId ? parseInt(pickupLocationId) : null,
        DropoffLocationID: dropoffLocationId ? parseInt(dropoffLocationId) : null
      };
      
      // Log booking data for debugging
      console.log('Creating booking with discounted values:');
      console.log('Original ServiceCharge (per seat):', originalServiceCharge);
      console.log('Discounted ServiceCharge (per seat, 15% off):', bookingData.ServiceCharge);
      console.log('TicketPrice (per seat):', bookingData.TicketPrice);
      console.log('NoOfTiket:', bookingData.NoOfTiket);
      console.log('TotalServiceCharge (discounted):', bookingData.TotalServiceCharge);
      console.log('TotalAmount (with discounted service charge):', bookingData.TotalAmount);
      console.log('Calculation: (', bookingData.TicketPrice, '+', bookingData.ServiceCharge, ') *', selectedSeats.length, '=', 
                  (bookingData.TicketPrice + bookingData.ServiceCharge) * selectedSeats.length);
      // Log UserID for debugging (especially important for cloud deployment)
      console.log('Creating booking with UserID:', bookingData.UserID, 'User object:', user, 'UserId from localStorage fallback:', userId);

      // Validate booking data before sending
      if (!bookingData.CustomerID || bookingData.CustomerID <= 0) {
        throw new Error(`Invalid Customer ID: ${customerId}`);
      }
      if (!bookingData.ScheduleDateID || bookingData.ScheduleDateID <= 0) {
        throw new Error(`Invalid Schedule Date ID: ${journeyDetails.scheduleDateId}`);
      }
      if (!bookingData.TravelDate) {
        throw new Error('Travel date is missing.');
      }
      // Validate UserID is present (critical for cloud deployment)
      if (!bookingData.UserID || bookingData.UserID <= 0) {
        console.error('UserID is missing in booking data. User object:', user);
        throw new Error('User ID is missing. Please ensure you are logged in and try again. If the problem persists, please log out and log back in.');
      }


      const bookingResponse = await bookingApi.createBooking(bookingData);
      const booking = bookingResponse?.value || bookingResponse?.data || bookingResponse;
      const bookingId = booking?.bookingID || booking?.BookingID;

      if (!bookingId) {
        throw new Error('Failed to create booking');
      }

      // Step 4: Create BookingSeats records for each selected seat
      const createdBookingSeats = [];
      
      for (const seatNumber of selectedSeats) {
        // Find or create seat
        const seatResponse = await seatApi.findOrCreateSeat({
          BusID: vehicleId,
          SeatNumber: seatNumber.toString(),
          SeatType: 'Standard',
          Price: basePrice
        });

        let seat = null;
        if (seatResponse?.value) {
          seat = seatResponse.value;
        } else if (Array.isArray(seatResponse)) {
          seat = seatResponse[0];
        } else if (typeof seatResponse === 'object') {
          seat = seatResponse;
        }

        const seatId = seat?.seatID || seat?.SeatID;
        if (!seatId) {
          throw new Error(`Failed to create or find seat ${seatNumber}`);
        }

        // Create BookingSeat record (if backend supports it)
        try {
          // Create BookingSeat record - REQUIRED (all seat info must be in BookingSeats table)
          const bookingSeatData = {
            BookingID: parseInt(bookingId),
            SeatID: parseInt(seatId),
            SeatNumber: seatNumber.toString(),
            Price: basePrice
          };

          const bookingSeatResponse = await bookingApi.addBookingSeat(bookingId, bookingSeatData);
          const bookingSeat = bookingSeatResponse?.value || bookingSeatResponse?.data || bookingSeatResponse;
          
          if (!bookingSeat) {
            throw new Error(`Failed to create BookingSeat for seat ${seatNumber}`);
          }
          
          createdBookingSeats.push(bookingSeat);
        } catch (error) {
          // Check if seat is already booked (availability conflict)
          const errorMessage = error?.message || error?.details?.error || error?.details?.message || '';
          const errorDetails = error?.details || error?.response?.data || {};
          const errorText = (typeof errorDetails === 'string' ? errorDetails : errorDetails.error || errorDetails.message || '').toLowerCase();
          const messageText = errorMessage.toLowerCase();
          
          // Check if this is a "seat already booked" error
          const isSeatAlreadyBooked = 
            messageText.includes('already booked') || 
            messageText.includes('seat is already') ||
            messageText.includes('seat already') ||
            errorText.includes('already booked') ||
            errorText.includes('seat is already') ||
            errorText.includes('seat already') ||
            error?.response?.status === 400 && (messageText.includes('seat') || errorText.includes('seat'));
          
          if (isSeatAlreadyBooked) {
            // Seat is already booked - this is a critical error that should stop the booking
            throw new Error(`Seat ${seatNumber} is already booked. Please select a different seat.`);
          }
          
          // For other errors, log and re-throw to handle in outer catch block
          console.error(`Error creating BookingSeat for seat ${seatNumber}:`, error);
          throw new Error(`Failed to book seat ${seatNumber}: ${errorMessage || 'Unknown error'}`);
        }
      }

      // Use booking ID as order ID

      // Use booking ID as order ID
      const orderId = `BK${bookingId.toString().padStart(6, '0')}`;

      Swal.close();
      
      // Prepare booking data to be used after payment
      // totalAmount already includes discount (same as payment gateway amount)
      const bookingDataForPayment = {
        passengerDetails,
        journeyDetails,
        selectedSeats,
        selectedDate,
        totalAmount, // Total with discount (same as payment gateway and database)
        basePrice,
        bookingId: bookingId, // Single booking ID for all seats
        bookingSeatIds: createdBookingSeats.map(bs => bs.bookingSeatID || bs.BookingSeatID),
        customerId
      };

      // Store pending booking data
      setPendingBookingData(bookingDataForPayment);
      setPaymentOrderId(orderId);

      // Show payment modal
      setShowPaymentModal(true);
    } catch (error) {
      console.error('Error creating booking:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data,
        details: error.details
      });
      
      // Extract detailed error message from backend
      let errorMessage = 'Failed to create booking. Please try again.';
      
      // Check error.details first (from ApiService)
      if (error.details) {
        if (typeof error.details === 'string') {
          errorMessage = error.details;
        } else if (error.details.message) {
          errorMessage = error.details.message;
        } else if (error.details.error) {
          errorMessage = error.details.error;
        } else if (error.details.title) {
          errorMessage = error.details.title;
        } else if (Array.isArray(error.details)) {
          // Validation errors array
          errorMessage = error.details.map(err => err.message || err).join(', ');
        } else if (error.details.raw) {
          errorMessage = error.details.raw;
        }
      }
      // Check error.response.data
      else if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.title) {
          errorMessage = error.response.data.title;
        } else if (Array.isArray(error.response.data)) {
          // Validation errors array
          errorMessage = error.response.data.map(err => err.message || err).join(', ');
        }
      }
      // Check error.message
      else if (error.message) {
        errorMessage = error.message;
      }
      
      // Show detailed error message
      Swal.fire({
        icon: 'error',
        title: 'Booking Failed',
        html: `
          <p style="margin-bottom: 10px;">${errorMessage}</p>
          ${error.response?.status ? `<p class="text-sm text-gray-500 mt-2">HTTP Status: ${error.response.status}</p>` : ''}
          <p class="text-xs text-gray-400 mt-2">Please check your booking details and try again.</p>
        `,
        confirmButtonColor: '#dc2626',
        width: '500px'
      });
    }
  };

  // This function is called after successful payment
  const handlePaymentSuccess = async (paymentResult) => {
    setShowPaymentModal(false);

    if (!pendingBookingData) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Booking data not found. Please try again.',
        confirmButtonColor: '#dc2626'
      });
      return;
    }

    try {
      // Show loading
      Swal.fire({
        title: 'Processing Booking...',
        text: 'Please wait while we confirm your booking.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const { passengerDetails, journeyDetails, selectedSeats, selectedDate, basePrice, totalAmount, bookingId } = pendingBookingData;

      // IMPORTANT: Booking was already created in handleBooking with PaymentStatus="Pending"
      // We just need to update the payment status to "Completed"
      if (!bookingId) {
        throw new Error('Booking ID not found. Please try booking again.');
      }


      // Step 1: Update payment status to "Completed" (non-blocking - don't fail if this errors)
      let paymentStatusUpdated = false;
      try {
        await bookingApi.updatePaymentStatus(bookingId, "Completed");
        paymentStatusUpdated = true;
      } catch (paymentStatusError) {
        console.error('Error updating payment status:', paymentStatusError);
        // Don't throw error - payment was successful, we'll show success message anyway
        // The booking exists and payment was processed, so we continue with success flow
      }

      // Use the existing booking ID for receipt generation
      const firstBookingId = bookingId;

      // Step 2: Send confirmation SMS (backend should handle this)
      // The backend will send SMS automatically after booking creation

      // Step 3: Show success with booking details and download receipt option
      Swal.close(); // Close loading dialog
        
        // Format route information - use journeyDetails.route which is already formatted
        const routeInfo = journeyDetails.route || 'N/A';
        
        // Format travel date
        const formattedDate = new Date(selectedDate).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        
        // Format departure time
        const departureTime = journeyDetails.departure || 'N/A';
        
        const result = await Swal.fire({
          icon: 'success',
          title: 'Payment Successful!',
          html: `
            <div class="text-left">
              <p class="mb-3 text-center font-semibold text-lg">Your booking has been confirmed!</p>
              
              <div class="bg-gray-50 p-4 rounded-lg mb-3">
                <p class="mb-2"><strong>Booking Reference:</strong> <span class="text-blue-600 font-semibold">BK${firstBookingId.toString().padStart(6, '0')}</span></p>
                <p class="mb-2"><strong>Payment Status:</strong> <span class="text-green-600 font-semibold">Completed</span></p>
              </div>
              
              <div class="border-t pt-3 mb-3">
                <p class="mb-1"><strong>Route:</strong> ${routeInfo}</p>
                <p class="mb-1"><strong>Travel Date:</strong> ${formattedDate}</p>
                <p class="mb-1"><strong>Departure Time:</strong> ${departureTime}</p>
                <p class="mb-1"><strong>Seats Booked:</strong> <span class="font-semibold">${selectedSeats.join(', ')}</span> (${selectedSeats.length} seat${selectedSeats.length > 1 ? 's' : ''})</p>
                <p class="mb-1"><strong>Total Amount:</strong> <span class="font-semibold text-green-600">Rs ${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></p>
              </div>
              
              <p class="text-sm text-gray-600 text-center mt-3">Confirmation SMS will be sent shortly.</p>
            </div>
          `,
          confirmButtonColor: '#2563eb',
          confirmButtonText: 'Download Receipt',
          showCancelButton: true,
          cancelButtonText: 'View My Bookings',
          cancelButtonColor: '#6b7280',
          width: '500px'
        });

        // Download receipt if user clicks "Download Receipt"
        if (result.isConfirmed && firstBookingId) {
          try {
            Swal.fire({
              title: 'Downloading Receipt...',
              text: 'Please wait',
              allowOutsideClick: false,
              didOpen: () => {
                Swal.showLoading();
              }
            });

            const receiptBlob = await bookingApi.getBookingReceipt(firstBookingId);
            const bookingRef = `BK${firstBookingId.toString().padStart(6, '0')}`;
            let url;
            
            // Check if it's HTML or PDF
            if (receiptBlob.type === 'text/html' || receiptBlob.type === 'text/html; charset=utf-8') {
              // Read HTML content and update title tag
              const htmlText = await receiptBlob.text();
              let updatedHtml = htmlText.replace(
                /<title>.*?<\/title>/i,
                `<title>Booking Receipt_${bookingRef}</title>`
              );
              // If no title tag exists, add one in the head section
              if (!updatedHtml.includes('<title>')) {
                updatedHtml = updatedHtml.replace(
                  /<head>/i,
                  `<head><title>Booking Receipt_${bookingRef}</title>`
                );
              }
              const updatedBlob = new Blob([updatedHtml], { type: 'text/html' });
              url = window.URL.createObjectURL(updatedBlob);
              
              // Open HTML in new window for printing
              const newWindow = window.open(url, '_blank');
              if (newWindow) {
                newWindow.onload = () => {
                  setTimeout(() => {
                    newWindow.print();
                  }, 500);
                };
              }
              // Also download as HTML
              const a = document.createElement('a');
              a.href = url;
              a.download = `Booking Receipt_${bookingRef}.html`;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
            } else {
              // Download as PDF
              url = window.URL.createObjectURL(receiptBlob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `Booking Receipt_${bookingRef}.pdf`;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
            }
            
            Swal.close();
            Swal.fire({
              icon: 'success',
              title: 'Receipt Downloaded',
              text: 'Your receipt has been downloaded successfully.',
              confirmButtonColor: '#2563eb',
              confirmButtonText: 'View My Bookings'
            }).then(() => {
              // Clear pending booking data before redirecting
              setPendingBookingData(null);
              setPaymentOrderId(null);
              setShowPaymentModal(false);
              navigate('/bookings');
            });
          } catch (receiptError) {
            console.error('Error downloading receipt:', receiptError);
            Swal.close();
            Swal.fire({
              icon: 'warning',
              title: 'Receipt Download Failed',
              text: 'Booking was successful, but receipt download failed. You can download it later from your bookings.',
              confirmButtonColor: '#2563eb',
              confirmButtonText: 'View My Bookings'
            }).then(() => {
              // Clear pending booking data before redirecting
              setPendingBookingData(null);
              setPaymentOrderId(null);
              setShowPaymentModal(false);
              navigate('/bookings');
            });
          }
        } else if (result.isDismissed && result.dismiss === Swal.DismissReason.cancel) {
          // User clicked "View My Bookings"
          // Clear pending booking data before redirecting
          setPendingBookingData(null);
          setPaymentOrderId(null);
          setShowPaymentModal(false);
          navigate('/bookings');
        } else {
          // User closed the dialog - redirect to My Bookings
          // Clear pending booking data before redirecting
          setPendingBookingData(null);
          setPaymentOrderId(null);
          setShowPaymentModal(false);
          navigate('/bookings');
        }

      // Clear pending booking data and close payment modal
      setPendingBookingData(null);
      setPaymentOrderId(null);
      setShowPaymentModal(false);
    } catch (error) {
      console.error('Error creating booking:', error);
      Swal.fire({
        icon: 'error',
        title: 'Booking Failed',
        text: error.message || 'An error occurred while processing your booking. Please try again.',
        confirmButtonColor: '#dc2626',
        confirmButtonText: 'OK'
      });
      
      // Clear pending booking data on error
      setPendingBookingData(null);
      setPaymentOrderId(null);
    }
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    setShowPaymentModal(false);
    setPendingBookingData(null);
    setPaymentOrderId(null);
  };

  const handleLoginSuccess = (userData) => {
    // User state is now managed by AuthContext via OtpLoginModal
    // Just close the modal
    setShowLoginModal(false);
    
    // Scroll to Book Now button after a short delay to ensure DOM is updated
    setTimeout(() => {
      if (bookButtonRef.current) {
        bookButtonRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }, 300);
  };

  // Filter routes for dropdown
  const filteredRoutes = routes
    .filter(route => {
      const routeName = `${route.fromLocation || route.fromCityName || ''} → ${route.toLocation || route.toCityName || ''}`;
      return routeName.toLowerCase().includes(routeSearchTerm.toLowerCase());
    })
    .sort((a, b) => {
      const nameA = `${a.fromLocation || a.fromCityName || ''} → ${a.toLocation || a.toCityName || ''}`;
      const nameB = `${b.fromLocation || b.fromCityName || ''} → ${b.toLocation || b.toCityName || ''}`;
      return nameA.localeCompare(nameB);
    });

  const selectedRoute = routes.find(r => 
    (r.routeID || r.routeId)?.toString() === selectedRouteId
  );

  const getRouteDisplayName = (route) => {
    return `${route.fromLocation || route.fromCityName || 'Unknown'} → ${route.toLocation || route.toCityName || 'Unknown'}`;
  };

  const totalPrice = journeyDetails 
    ? selectedSeats.length * (parseFloat(journeyDetails.basePrice) || 0)
    : 0;
  
  // Calculate discount for UI display (15% of total service charge)
  const totalServiceChargeForUI = journeyDetails 
    ? (journeyDetails.serviceCharge || 0) * selectedSeats.length
    : 0;
  const discountAmount = totalServiceChargeForUI * 0.15;
  const totalWithDiscount = totalPrice + totalServiceChargeForUI - discountAmount;

  return (
    <div className="w-screen min-w-screen max-w-screen min-h-screen overflow-x-hidden bg-gray-50">
      {/* OTP Login Modal */}
      <OtpLoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={handleLoginSuccess}
        initialName={passengerDetails.name || ''}
        initialPhone={passengerDetails.phone || ''}
      />

      {/* Mastercard Payment Modal */}
      {showPaymentModal && pendingBookingData && (
        <MastercardPaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setPendingBookingData(null);
            setPaymentOrderId(null);
          }}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
          amount={pendingBookingData.totalAmount}
          currency="LKR"
          orderId={paymentOrderId}
          customerInfo={{
            name: pendingBookingData.passengerDetails.name,
            email: pendingBookingData.passengerDetails.email,
            phone: pendingBookingData.passengerDetails.phone
          }}
          bookingData={pendingBookingData}
        />
      )}
      {/* Header Section - Very Compact */}
      <section className="w-screen min-w-screen max-w-screen bg-gradient-to-r from-blue-600 to-blue-800 text-white py-2 lg:py-3">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-1">
              Book Your Bus Ticket
            </h1>
            <p className="text-xs sm:text-sm text-blue-100">
              Select your route, date, seat and pay to complete your booking
            </p>
          </div>
        </div>
      </section>

      <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto">
          {/* Available Schedules Cards - Full Width */}
          {availableSchedules.length > 1 && (
            <div className="bg-white rounded-2xl shadow-lg p-3 mb-4">
              <h2 className="text-base font-bold text-gray-900 mb-2">Available Schedules</h2>
              <div className="flex flex-wrap gap-2">
                {availableSchedules.map((scheduleData, index) => (
                  <div
                    key={scheduleData.scheduleId || index}
                    onClick={() => handleScheduleSelect(scheduleData)}
                    className={`border-2 rounded-lg px-3 py-2 cursor-pointer transition-all duration-200 ${
                      selectedScheduleId === scheduleData.scheduleId
                        ? 'border-blue-600 bg-blue-100 shadow-md'
                        : 'border-gray-200 hover:border-blue-400 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <div className="text-xs font-semibold text-gray-700 mb-1">{scheduleData.scheduleName}</div>
                    <div className="flex items-center space-x-3 text-xs">
                      <div className="flex items-center space-x-1">
                        <Clock className="w-3 h-3 text-blue-600" />
                        <span className="font-medium text-gray-900">{scheduleData.departure}</span>
                      </div>
                      <span className="text-gray-400">→</span>
                      <div className="flex items-center space-x-1">
                        <Clock className="w-3 h-3 text-green-600" />
                        <span className="font-medium text-gray-900">{scheduleData.arrival}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Route/Date Selection and Seat Selection */}
            <div className="lg:col-span-2 space-y-4">
              {/* Route and Date Selection - Same width as Seat Selection */}
              <div className="bg-white rounded-2xl shadow-lg p-4">
                <h2 className="text-lg font-bold text-gray-900 mb-3">Select Route and Date</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Route Selector */}
              <div className="relative" ref={routeDropdownRef}>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      <MapPin className="w-3 h-3 inline mr-1 text-blue-600" />
                  Select Route
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={selectedRoute ? getRouteDisplayName(selectedRoute) : ''}
                    readOnly
                    onClick={() => setRouteDropdownOpen(!routeDropdownOpen)}
                    placeholder={routesLoading ? 'Loading routes...' : 'Select a route'}
                        className="w-full px-3 py-2 border border-blue-400 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-blue-500 bg-blue-50 text-blue-900 text-sm transition-all duration-200 pr-8 cursor-pointer"
                    disabled={routesLoading}
                    required
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                    {selectedRouteId && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRouteId('');
                          setRouteSearchTerm('');
                          setJourneyDetails(null);
                          setSeats([]);
                          setSelectedSeats([]);
                        }}
                        className="text-blue-500 hover:text-blue-700 transition-colors"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    )}
                    <ChevronDown className={`w-4 h-4 text-blue-500 transition-transform ${routeDropdownOpen ? 'rotate-180' : ''}`} />
                  </div>
                </div>
                
                {routeDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-blue-200 rounded-xl shadow-lg max-h-60 overflow-hidden">
                    {/* Search box */}
                    <div className="p-3 border-b border-blue-100 bg-blue-50">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-400" />
                        <input
                          type="text"
                          value={routeSearchTerm}
                          onChange={(e) => setRouteSearchTerm(e.target.value)}
                          placeholder="Search routes..."
                          className="w-full pl-10 pr-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-blue-500 bg-white text-blue-900 text-sm"
                          autoFocus
                        />
                      </div>
                    </div>
                    
                    {/* Options list */}
                    <div className="max-h-48 overflow-y-auto">
                      {filteredRoutes.length > 0 ? (
                        filteredRoutes.map(route => {
                          const routeId = route.routeID || route.routeId;
                          return (
                            <div
                              key={routeId}
                              onClick={() => {
                                setSelectedRouteId(routeId.toString());
                                setRouteSearchTerm('');
                                setRouteDropdownOpen(false);
                              }}
                              className="px-4 py-3 hover:bg-blue-50 cursor-pointer transition-colors text-blue-900 border-b border-blue-100 last:border-b-0"
                            >
                              {getRouteDisplayName(route)}
                            </div>
                          );
                        })
                      ) : (
                        <div className="px-4 py-3 text-gray-500 text-center">
                          No routes found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Date Selector */}
              <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      <Calendar className="w-3 h-3 inline mr-1 text-blue-600" />
                  Select Date
                </label>
                <input
                  type="date"
                  value={selectedDate || ''}
                  onChange={(e) => {
                    const selectedDateValue = e.target.value;
                    const today = getMinUTCDate();
                    if (selectedDateValue >= today) {
                      setSelectedDate(selectedDateValue);
                    } else {
                      Swal.fire({
                        icon: 'warning',
                        title: 'Invalid Date',
                        text: 'Please select today\'s date or a future date.',
                        confirmButtonColor: '#3085d6'
                      });
                    }
                  }}
                  min={getMinUTCDate()}
                  max={getMaxSelectableDate()}
                      className="w-full px-3 py-2 border border-blue-400 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-blue-500 bg-blue-50 text-blue-900 text-sm transition-all duration-200 cursor-pointer"
                  required
                  style={{
                    colorScheme: 'light'
                  }}
                />
              </div>
            </div>

            {loadingJourney && (
              <div className="mt-4 flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-blue-600 mr-2" />
                <span className="text-blue-600">Loading journey details...</span>
              </div>
            )}

            {/* No Schedule Message - Show when route and date are selected but no schedules found (only for future dates, not today) */}
            {!loadingJourney && selectedRouteId && selectedDate && availableSchedules.length === 0 && !journeyDetails && selectedDate !== getTodayUTCDate() && (
              <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                <Bus className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-600 font-medium">No schedule</p>
                <p className="text-sm text-gray-500 mt-1">No schedules available for the selected date.</p>
              </div>
            )}
          </div>
              {/* Seat Selection - Always visible when journey details available */}
              {journeyDetails && (
                <div className="bg-white rounded-2xl shadow-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-gray-900">Select Your Seats</h3>
                    {journeyDetails.busNumber && (
                      <div className="flex items-center space-x-2 text-sm">
                        <Bus className="w-4 h-4 text-blue-600" />
                        <span className="font-semibold text-blue-600">
                          {journeyDetails.busNumber}
                        </span>
            </div>
          )}
                  </div>
                  
                  {/* Compact Journey Info */}
                  <div className="grid grid-cols-4 gap-2 mb-3 p-2 bg-blue-50 rounded-lg text-xs">
                  <div className="text-center">
                      <p className="text-gray-500">Route</p>
                      <p className="font-semibold text-gray-900 truncate">{journeyDetails.route}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-gray-500">Date</p>
                    <p className="font-semibold text-gray-900">{journeyDetails.date}</p>
                  </div>
                  <div className="text-center">
                      <p className="text-gray-500">Time</p>
                      <p className="font-semibold text-gray-900">
                        {journeyDetails.departure || 'N/A'}
                        {journeyDetails.arrival && (
                          <span className="text-green-600"> &gt; {journeyDetails.arrival}</span>
                        )}
                      </p>
                  </div>
                  <div className="text-center">
                      <p className="text-gray-500">Price</p>
                      <p className="font-semibold text-blue-600">{journeyDetails.price}</p>
                  </div>
                </div>

                  {/* Seat Legend - Compact */}
                  <div className="grid grid-cols-6 gap-2 mb-3">
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-3 bg-green-500 rounded"></div>
                      <span className="text-xs text-gray-600">Available</span>
                  </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-3 bg-blue-600 rounded"></div>
                      <span className="text-xs text-gray-600">Selected</span>
                  </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-3 bg-yellow-500 rounded"></div>
                      <span className="text-xs text-gray-600">Available Soon</span>
                </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-3 bg-red-500 rounded"></div>
                      <span className="text-xs text-gray-600">Gents</span>
                </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-3 bg-pink-500 rounded"></div>
                      <span className="text-xs text-gray-600">Ladies</span>
              </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-3 bg-gray-400 rounded"></div>
                      <span className="text-xs text-gray-600">N/A</span>
                </div>
                  </div>
                  
                  {/* Seat Layout - Compact */}
                  <div className="border-2 border-gray-200 rounded-lg p-3 bg-gray-50">
                    {/* Rear Door - At Top */}
                    <div className="text-center mb-2">
                      <div className="inline-block bg-gray-300 text-gray-600 px-2 py-1 rounded text-xs">
                        Rear
                      </div>
                    </div>

                    {/* Seats Grid */}
                    {seats.length > 0 ? (() => {
                      // Parse seat layout to determine columns and rows
                      const layoutMatch = journeyDetails.seatLayout?.match(/(\d+)[x×](\d+)/i);
                      const columns = layoutMatch ? parseInt(layoutMatch[1]) : 4;
                      const rows = layoutMatch ? parseInt(layoutMatch[2]) : 12;
                      
                      // Group seats by column
                      const seatsByColumn = {};
                      seats.forEach(seat => {
                        if (!seatsByColumn[seat.col]) {
                          seatsByColumn[seat.col] = [];
                        }
                        seatsByColumn[seat.col].push(seat);
                      });
                      
                      // Sort columns
                      const sortedColumns = Object.keys(seatsByColumn).sort((a, b) => parseInt(a) - parseInt(b));
                      
                      // For 4 columns: left side (1,2), gap, right side (3,4)
                      const leftColumns = sortedColumns.filter(col => parseInt(col) <= 2);
                      const rightColumns = sortedColumns.filter(col => parseInt(col) > 2);
                      
                      return (
                        <div className="flex justify-center items-start gap-4">
                          {/* Left side: 2 columns */}
                          <div className="flex gap-2">
                            {leftColumns.map(colNum => {
                              const columnSeats = seatsByColumn[colNum].sort((a, b) => a.row - b.row);
                              const isFirstColumn = parseInt(colNum) === Math.min(...leftColumns.map(c => parseInt(c)));
                              // Find seat 01 in this column
                              const seat01Index = columnSeats.findIndex(seat => seat.number === '01' || seat.number === '1');
                              
                              return (
                                <div key={colNum} className="flex flex-col items-center gap-2">
                                  <div className="mb-2 h-6"></div>
                                  {columnSeats.map((seat, seatIndex) => {
                                    const isSeat01 = seat.number === '01' || seat.number === '1';
                                    return (
                                      <React.Fragment key={seat.id}>
                        <button
                          onClick={() => handleSeatClick(seat)}
                          disabled={!seat.isAvailable || seat.isNotAvailable}
                                          className={`w-10 h-10 rounded-lg font-medium text-xs ${getSeatClass(seat)}`}
                          title={
                            seat.isAvailableSoon
                              ? `Seat ${seat.number} - Available Soon`
                              : !seat.isAvailable && !seat.isNotAvailable 
                              ? `Seat ${seat.number} - Booked` 
                              : seat.isNotAvailable 
                              ? `Seat ${seat.number} - Not Available`
                              : selectedSeats.includes(seat.id)
                              ? `Seat ${seat.number} - Click to deselect`
                              : `Seat ${seat.number} - Click to select`
                          }
                        >
                          {seat.number}
                        </button>
                                        {/* Show wheel icon below (after) seat 01 */}
                                        {isSeat01 && isFirstColumn && (
                                          <div className="mt-1">
                                            <img 
                                              src="/images/wheel.svg" 
                                              alt="Steering Wheel" 
                                              className="w-6 h-6"
                                              onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                </div>
                                        )}
                                      </React.Fragment>
                              );
                            })}
                    </div>
                              );
                            })}
                          </div>
                          
                          {/* Right side: 2 columns */}
                          <div className="flex gap-2">
                            {rightColumns.map((colNum, index) => {
                              const columnSeats = seatsByColumn[colNum].sort((a, b) => a.row - b.row);
                              
                              return (
                                <div key={colNum} className="flex flex-col items-center gap-2">
                                  <div className="mb-2 h-6"></div>
                                  
                                  {/* Seats in this column */}
                                  {columnSeats.map((seat) => (
                                    <button
                                      key={seat.id}
                                      onClick={() => handleSeatClick(seat)}
                                      disabled={!seat.isAvailable || seat.isNotAvailable || seat.isAvailableSoon}
                                      className={`w-10 h-10 rounded-lg font-medium text-xs ${getSeatClass(seat)}`}
                                      title={
                                        seat.isAvailableSoon
                                          ? `Seat ${seat.number} - Available Soon`
                                          : !seat.isAvailable && !seat.isNotAvailable 
                                          ? `Seat ${seat.number} - Booked` 
                                          : seat.isNotAvailable 
                                          ? `Seat ${seat.number} - Not Available`
                                          : selectedSeats.includes(seat.id)
                                          ? `Seat ${seat.number} - Click to deselect`
                                          : `Seat ${seat.number} - Click to select`
                                      }
                                    >
                                      {seat.number}
                                    </button>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })() : (
                      <div className="text-center py-8 text-gray-500">
                        No seats available
                      </div>
                    )}
                  </div>
                      </div>
                    )}


                      </div>

            {/* Right Column - Booking Summary */}
            <div className="space-y-4">
              {/* Price Summary - At the top */}
              {journeyDetails && (
              <div className="bg-white rounded-2xl shadow-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Price Summary</h3>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Bus fare ({journeyDetails.price} × {selectedSeats.length})</span>
                    <span className="font-medium">Rs {totalPrice.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Service Fee (Rs {(journeyDetails.serviceCharge || 0).toFixed(2)} × {selectedSeats.length})</span>
                    <span className="font-medium">Rs {((journeyDetails.serviceCharge || 0) * selectedSeats.length).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Discount By Tripimate (15% on Service Fee)</span>
                    <span className="font-medium text-green-600">- Rs {(((journeyDetails.serviceCharge || 0) * selectedSeats.length) * 0.15).toFixed(2)}</span>
                  </div>
                  <hr className="my-2" />
                  <div className="flex justify-between text-base font-bold">
                    <span>Total</span>
                    <span className="text-blue-600">Rs {totalWithDiscount.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              )}

              {/* Selected Seats Summary */}
              {journeyDetails && (
              <div className="bg-white rounded-2xl shadow-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Selected Seats</h3>
                
                {selectedSeats.length === 0 ? (
                  <p className="text-gray-500 text-center py-4 text-sm">No seats selected</p>
                ) : (
                  <div className="space-y-2">
                    {selectedSeats.map((seatId) => {
                      const seat = seats.find(s => s.id === seatId);
                      const seatName = seat ? seat.number : seatId;
                      return (
                      <div key={seatId} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                        <span className="font-medium text-blue-900 text-sm">Seat {seatName}</span>
                        <button
                            onClick={() => {
                              setSelectedSeats(prev => prev.filter(s => s !== seatId));
                              setSeats(prev => prev.map(s => 
                                s.id === seatId ? { ...s, isSelected: false } : s
                              ));
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors"
                            title="Remove seat"
                          >
                            <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                    })}
                  </div>
                )}
                </div>
              )}

              {/* Passenger Details Form - Moved to right column below Price Summary */}
              {journeyDetails && (
                <div className="bg-white rounded-2xl shadow-lg p-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Passenger Information</h3>
                  
                  <form className="space-y-2">
                    {/* Top row: Name, Phone Number, Gender */}
                    <div className="grid grid-cols-1 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={passengerDetails.name}
                          onChange={handlePassengerDetailsChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={passengerDetails.phone}
                          onChange={handlePassengerDetailsChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Gender *
                        </label>
                        <select
                          name="gender"
                          value={passengerDetails.gender}
                          onChange={handlePassengerDetailsChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          required
                        >
                          <option value="">Select Gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                        </select>
                      </div>
                    </div>
                    
                    {/* Email and NIC fields are hidden but will be sent with hardcoded empty values */}

                    {/* Pickup and Dropoff Locations */}
                      <div className="grid grid-cols-1 gap-2">
                      <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Pickup Location <span className="text-red-500">*</span>
                        </label>
                          <select
                            value={pickupLocationId}
                            onChange={(e) => setPickupLocationId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            disabled={routeCitiesLoading}
                            required
                          >
                            <option value="">Select Pickup Location</option>
                            {routeCities.length > 0 ? (
                              routeCities.map((routeCity) => (
                              <option key={routeCity.cityID || routeCity.CityID} value={routeCity.cityID || routeCity.CityID}>
                                {routeCity.city?.cityName || routeCity.City?.CityName || routeCity.cityName || routeCity.CityName || 'Unknown'}
                              </option>
                              ))
                            ) : (
                              <option value="" disabled>No locations available</option>
                            )}
                          </select>
                      </div>
                      
                      <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Dropoff Location <span className="text-red-500">*</span>
                        </label>
                          <select
                            value={dropoffLocationId}
                            onChange={(e) => setDropoffLocationId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            disabled={routeCitiesLoading}
                            required
                          >
                            <option value="">Select Dropoff Location</option>
                            {routeCities.length > 0 ? (
                              routeCities.map((routeCity) => (
                              <option key={routeCity.cityID || routeCity.CityID} value={routeCity.cityID || routeCity.CityID}>
                                {routeCity.city?.cityName || routeCity.City?.CityName || routeCity.cityName || routeCity.CityName || 'Unknown'}
                              </option>
                              ))
                            ) : (
                              <option value="" disabled>No locations available</option>
                            )}
                          </select>
                      </div>
                    </div>

                    {/* Special Request - Hidden per requirement */}
                    <div style={{ display: 'none' }}>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Special Request <span className="text-gray-400 text-xs">(Optional)</span>
                      </label>
                      <textarea
                        name="specialRequest"
                        value={passengerDetails.specialRequest}
                        onChange={handlePassengerDetailsChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="Any special requests or notes..."
                        rows="2"
                      />
                    </div>

                    <div className="pt-2">
                      <button
                        ref={bookButtonRef}
                        type="button"
                        onClick={handleBooking}
                        disabled={selectedSeats.length === 0}
                        className={`w-full px-4 rounded-lg text-sm font-semibold transition-all ${
                          user 
                            ? 'bg-red-600 text-white py-4 hover:bg-red-700 shadow-lg shadow-red-500/50 ring-2 ring-red-400 ring-offset-2' 
                            : 'bg-blue-600 text-white py-2 hover:bg-blue-700'
                        } disabled:bg-gray-400 disabled:cursor-not-allowed disabled:shadow-none disabled:ring-0`}
                      >
                        {selectedSeats.length === 0 
                          ? 'Select Seats First' 
                          : user 
                            ? 'Pay Now' 
                            : 'Book Now'
                        }
                      </button>
                      <p className="text-[10px] text-gray-500 text-center mt-2">
                        By clicking {user ? '"Pay Now"' : '"Book Now"'}, I agree to the{' '}
                        <Link to="/terms" className="text-gray-600 hover:text-gray-800 underline">
                          Terms & Conditions
                        </Link>
                        {' '}and{' '}
                        <Link to="/ticket-policy" className="text-gray-600 hover:text-gray-800 underline">
                          Ticket Policy
                        </Link>
                      </p>
                    </div>
                  </form>
                </div>
              )}

                  </div>
                </div>
        </div>
      </div>
    </div>
  );
};

export default BusBooking;

